import { safeSetItem } from "@/lib/storage/safe-local";
/**
 * RoleRepository — backend-agnostic contract for role membership.
 *
 * The application never talks to a specific auth/database provider for
 * role checks. Every admin surface reads and writes roles through this
 * interface. Swapping the storage provider is a matter of registering a
 * different implementation via {@link setRoleRepository}.
 *
 * The default implementation persists to localStorage so development and
 * the RC1 baseline continue to work without any external service. Prompt
 * 21 will register a cloud-backed implementation over the same interface.
 */
import { useEffect, useState } from "react";

export type Role =
  | "super_admin"
  | "admin"
  | "manager"
  | "operations"
  | "read_only"
  | "content_editor"
  | "inventory_manager"
  | "marketing_manager"
  | "support_agent";

export const ROLE_LABEL: Record<Role, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  operations: "Operations",
  read_only: "Read Only",
  content_editor: "Content Editor",
  inventory_manager: "Inventory Manager",
  marketing_manager: "Marketing Manager",
  support_agent: "Support Agent",
};

export type RoleAssignment = {
  userId: string;
  email?: string | null;
  roles: Role[];
  /** ISO timestamp of the earliest grant, when the backend records one. */
  grantedAt?: string | null;
};

export interface RoleRepository {
  getUserRoles(userId: string): Promise<Role[]>;
  assignRole(userId: string, role: Role, email?: string | null): Promise<void>;
  removeRole(userId: string, role: Role): Promise<void>;
  listUsersWithRoles(): Promise<RoleAssignment[]>;
  hasRole(userId: string, role: Role): Promise<boolean>;
  /** True when at least one user in the system holds `super_admin`. */
  hasAnySuperAdmin(): Promise<boolean>;
  /** Wipe every assignment. Development tools only. */
  reset(): Promise<void>;
  /** Subscribe to change events. Returns an unsubscribe. */
  subscribe(listener: () => void): () => void;
}

// --------------------------------------------------------------------------
// Local implementation — the default. Uses a single localStorage key so all
// admin surfaces share one source of truth.
// --------------------------------------------------------------------------

const LS_KEY = "cb16.roles.repository.v1";
const EVT = "cb16:roles-repository";

type Store = {
  users: Record<string, { email?: string | null; roles: Role[] }>;
};

function read(): Store {
  if (typeof window === "undefined") return { users: {} };
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return migrateLegacy();
    return JSON.parse(raw) as Store;
  } catch {
    return { users: {} };
  }
}

function write(next: Store) {
  if (typeof window === "undefined") return;
  safeSetItem(LS_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event(EVT));
}

/**
 * Migrate legacy Prompt 17 storage into the unified repository.
 * - `cb16.admin.roles` — { [uid]: true } grants "admin"
 * - `cb16.admin.rbac.assignments.v1` — { [uid]: AdminRole[] } grants fine roles
 */
function migrateLegacy(): Store {
  const store: Store = { users: {} };
  if (typeof window === "undefined") return store;
  try {
    const legacyAdmin = JSON.parse(window.localStorage.getItem("cb16.admin.roles") ?? "{}") as Record<string, true>;
    for (const uid of Object.keys(legacyAdmin)) {
      store.users[uid] = { roles: ["admin"] };
    }
  } catch { /* ignore */ }
  try {
    const legacyRbac = JSON.parse(window.localStorage.getItem("cb16.admin.rbac.assignments.v1") ?? "{}") as Record<string, Role[]>;
    for (const [uid, roles] of Object.entries(legacyRbac)) {
      const cur = store.users[uid]?.roles ?? [];
      store.users[uid] = { roles: Array.from(new Set([...cur, ...roles])) as Role[] };
    }
  } catch { /* ignore */ }
  write(store);
  return store;
}

class LocalRoleRepository implements RoleRepository {
  async getUserRoles(userId: string): Promise<Role[]> {
    return read().users[userId]?.roles ?? [];
  }
  async assignRole(userId: string, role: Role, email?: string | null): Promise<void> {
    const s = read();
    const cur = s.users[userId] ?? { roles: [] as Role[] };
    const roles = Array.from(new Set([...cur.roles, role])) as Role[];
    s.users[userId] = { email: email ?? cur.email ?? null, roles };
    write(s);
  }
  async removeRole(userId: string, role: Role): Promise<void> {
    const s = read();
    const cur = s.users[userId];
    if (!cur) return;
    cur.roles = cur.roles.filter((r) => r !== role);
    if (cur.roles.length === 0) delete s.users[userId];
    else s.users[userId] = cur;
    write(s);
  }
  async listUsersWithRoles(): Promise<RoleAssignment[]> {
    const s = read();
    return Object.entries(s.users).map(([userId, v]) => ({
      userId, email: v.email ?? null, roles: v.roles,
    }));
  }
  async hasRole(userId: string, role: Role): Promise<boolean> {
    return (read().users[userId]?.roles ?? []).includes(role);
  }
  async hasAnySuperAdmin(): Promise<boolean> {
    const s = read();
    return Object.values(s.users).some((v) => v.roles.includes("super_admin"));
  }
  async reset(): Promise<void> {
    write({ users: {} });
  }
  subscribe(listener: () => void): () => void {
    if (typeof window === "undefined") return () => {};
    window.addEventListener(EVT, listener);
    window.addEventListener("storage", listener);
    return () => {
      window.removeEventListener(EVT, listener);
      window.removeEventListener("storage", listener);
    };
  }
}

// --------------------------------------------------------------------------
// Registry
// --------------------------------------------------------------------------

let repo: RoleRepository = new LocalRoleRepository();

export function getRoleRepository(): RoleRepository {
  return repo;
}
export function setRoleRepository(next: RoleRepository) {
  repo = next;
}

// --------------------------------------------------------------------------
// React helpers
// --------------------------------------------------------------------------

export function useUserRoles(userId: string | null | undefined): Role[] {
  const [roles, setRoles] = useState<Role[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!userId) { setRoles([]); return; }
    const load = () => {
      getRoleRepository().getUserRoles(userId).then((r) => { if (!cancelled) setRoles(r); });
    };
    load();
    const unsub = getRoleRepository().subscribe(load);
    return () => { cancelled = true; unsub(); };
  }, [userId]);
  return roles;
}

export function useHasAnySuperAdmin(): { ready: boolean; exists: boolean } {
  const [state, setState] = useState<{ ready: boolean; exists: boolean }>({ ready: false, exists: false });
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getRoleRepository().hasAnySuperAdmin().then((exists) => {
        if (!cancelled) setState({ ready: true, exists });
      });
    };
    load();
    const unsub = getRoleRepository().subscribe(load);
    return () => { cancelled = true; unsub(); };
  }, []);
  return state;
}

export function useRoleAssignments(): RoleAssignment[] {
  const [list, setList] = useState<RoleAssignment[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getRoleRepository()
        .listUsersWithRoles()
        .then((r) => { if (!cancelled) setList(r); })
        .catch((e: unknown) => {
          console.error("[admin] listUsersWithRoles failed:", e instanceof Error ? e.message : String(e));
        });
    };
    load();
    const unsub = getRoleRepository().subscribe(load);
    return () => { cancelled = true; unsub(); };
  }, []);
  return list;
}